import { Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import type { CollectionSubcategory } from '@/utils/collections/collections.types';

import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';

import { CategoryCard } from './components/CategoryCard/CategoryCard';

type Props = {
    /**
     * Crop each porthole to fill its circle instead of letterboxing it. On for the LAAC-2371
     * collections, off for the legacy star-wars page -- see the <img> comment in CategoryCard.
     */
    fillTiles?: boolean;
    /** Omitted for the LAAC-2371 design, which has no heading; supplied by the legacy star-wars page. */
    heading?: string;
    subcategories: CollectionSubcategory[];
};

export const RelatedCategories = ({ fillTiles = false, heading, subcategories }: Props) => {
    const isTabletSize = useIsNarrowerThan(mergedCaterwaulAndHammerUIThemes.breakpoints.tabletNarrowWidthNum);

    if (subcategories.length === 0) {
        return null;
    }

    if (isTabletSize) {
        const cards = subcategories.map((subcategory, index) => ({
            card: (
                // The carousel sizes its items to their content, so the 128px lives here. Without
                // it the tile would take its width from the label, which is the bug the design
                // feedback reported.
                <div className="w-[128px]">
                    <CategoryCard
                        fillTile={fillTiles}
                        href={subcategory.href}
                        imageUrl={subcategory.imageUrl}
                        position={index + 1}
                        title={subcategory.title}
                    />
                </div>
            ),
            key: subcategory.title,
        }));

        return (
            /*
             * `[&_.mt-sm]:mt-0` reaches into hammer-ui's Carousel to cancel the
             * `mt-sm` it hardcodes on its scroll container. With no `heading` the carousel
             * renders no title section, so that margin is dead space -- but the Carousel
             * exposes no className for that element, so a scoped child selector is the only
             * override that does not fork the package. If hammer-ui ever accepts a
             * `scrollContainerClassName` prop, pass `mt-0` through it and delete this.
             *
             * Constraint: the Carousel puts `mt-sm` on TWO elements -- the scroll container and,
             * under `showPagination`, the page-dots row. A descendant selector cannot tell them
             * apart, so turning on `showPagination` here would also flatten the gap above the dots.
             *
             * `[&_[role=list]]:gap-xs` halves the 16px `xsMax:gap-sm` the Carousel hardcodes between
             * cards, so more of the next porthole bleeds past the right edge and the row reads as
             * scrollable. Selected by role rather than by the compiled class name, which is an
             * implementation detail.
             *
             * KNOWN LIMITATION, measured -- do not assume this is safe. hammer-ui computes its page
             * breakpoints from a hardcoded `CAROUSEL_CARD_GAP_MOBILE_NUM = 16` while the DOM now
             * uses 8, and its `onKeyUp` handler sits on the scroll container itself, so a keyup
             * bubbling from any focused porthole link reaches it even though the arrow buttons are
             * `xsMax:hidden`. Consequences at 390px, both verified in a browser:
             *   - ArrowLeft/ArrowRight scroll to 304 where the card edge is 288: a 16px overshoot
             *     that clips a porthole. It landed flush before the gap changed.
             *   - The `aria-live` label reads `Slide N of 3` where the true page count is 2, so a
             *     screen-reader user hears a page total that does not match the layout.
             * Neither blocks navigation (Tab still reaches every porthole, and manual swipes are
             * never repositioned -- `onScrollCards` clears the reposition flag). The real fix is
             * upstream: have the Carousel read `getComputedStyle(contentRef.current).columnGap`
             * instead of the constant. Until then, changing this gap trades those two defects for
             * the bleed, which is the trade the design feedback asked for.
             */
            <div className={cn('pb-md [&_[role=list]]:gap-xs', !heading && '[&_.mt-sm]:mt-0')}>
                <Carousel
                    cards={cards}
                    title={heading}
                    useOverlayControls
                />
            </div>
        );
    }

    return (
        <div className="pb-md">
            {heading && <H2>{heading}</H2>}

            {/*
             * 128px is a floor, not a fixed width. `auto-fit` + `minmax(128px,1fr)` grows the tiles
             * to fill a wide desktop -- back to roughly the ~190px they were before this ticket --
             * and drops to fewer columns as the row narrows, so six tiles can never overflow the
             * 720px content box at 768px, where the carousel has not taken over yet.
             */}
            <div className="mt-sm grid grid-cols-[repeat(auto-fit,minmax(128px,1fr))] gap-md">
                {subcategories.map(({ href, imageUrl, title }, index) => (
                    <CategoryCard
                        fillTile={fillTiles}
                        href={href}
                        imageUrl={imageUrl}
                        key={title}
                        position={index + 1}
                        title={title}
                    />
                ))}
            </div>
        </div>
    );
};
